import React, { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { startCheckout } from "../lib/checkout";
import { getAccentVariant } from "../data/tiers";
import { TAP } from "../lib/motion";

const accentMap = {
  cyan: { text: "text-cyan", line: "#00D4FF" },
  gold: { text: "text-gold", line: "#D4AF37" },
  pro: { text: "text-cyan", line: "#00D4FF" },
};

const borderStyles = {
  pro: { border: "1px solid rgba(0,212,255,0.5)" },
  gold: { border: "1px solid rgba(212,175,55,0.28)" },
  default: { border: "1px solid var(--border)" },
};

const ctaClasses = { pro: "cta-primary", gold: "cta-gold", default: "cta-outline" };

const priceStyles = {
  pro: {
    background: "linear-gradient(90deg, #00D4FF, #00E676)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    filter: "drop-shadow(0 0 20px rgba(0,212,255,0.35))",
  },
  gold: {
    background: "linear-gradient(90deg, #D4AF37, #F1DE9C)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    filter: "drop-shadow(0 0 20px rgba(212,175,55,0.3))",
  },
  default: { color: "#fff", textShadow: "0 0 28px rgba(0,212,255,0.3)" },
};

function useCardTilt() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 200, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 200, damping: 20 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return { ref, rx, ry, onMove, onLeave };
}

const Badges = ({ tier }) => (
  <div className="flex flex-wrap gap-2 mb-6 min-h-[26px]">
    {tier.badges && tier.badges.map((b) => (
      <span key={b} className="eyebrow text-[0.6rem] px-3 py-1.5 bg-gradient-to-r from-[#00D4FF] to-[#00E676] text-[#05070a] font-600" data-testid={`badge-${tier.id}`}>
        {b}
      </span>
    ))}
    {tier.badge && (
      <span className="eyebrow text-[0.6rem] px-3 py-1.5 border border-[rgba(212,175,55,0.5)] text-gold" data-testid={`badge-${tier.id}`}>
        {tier.badge}
      </span>
    )}
  </div>
);

const PriceBlock = ({ tier, variant, isGold }) => (
  <>
    <div className="flex items-baseline justify-between">
      <h3 className={`font-display text-2xl font-700 tracking-wide ${isGold ? "text-gold" : "text-white"}`}>{tier.name}</h3>
    </div>
    <div className="mt-3 flex items-baseline gap-2" data-testid={`price-${tier.id}`}>
      <span className="font-display text-5xl sm:text-6xl font-700 tracking-tight" style={priceStyles[variant]}>${tier.price}</span>
      <span className="text-white/60 text-sm font-display tracking-widest">/ MONTH</span>
    </div>
  </>
);

const CreditBlock = ({ tier, accent }) => (
  <div className="mt-6 p-4 border-l-2" style={{ borderColor: accent.line, background: "rgba(255,255,255,0.02)" }}>
    <div className={`font-display text-2xl sm:text-3xl font-700 leading-tight ${accent.text}`}>{tier.creditHeadline[0]}</div>
    <div className="text-xs font-display tracking-[0.18em] text-white/80 mt-1">{tier.creditHeadline[1]}</div>
    <div className="mt-3 space-y-1">
      {tier.perks.map((p) => (
        <div key={p} className="font-display text-sm font-500 text-white">{p}</div>
      ))}
    </div>
  </div>
);

const Benefits = ({ tier, accent }) => (
  <ul className="mt-6 space-y-2.5 flex-1">
    {tier.benefits.map((b) => (
      <li key={b} className="flex items-start gap-2.5 text-sm text-white/85">
        <Check size={16} className={`mt-0.5 shrink-0 ${accent.text}`} strokeWidth={2.5} />
        <span>{b}</span>
      </li>
    ))}
  </ul>
);

const Callout = ({ tier }) => (
  <div className="mt-7 py-4 border-t border-b border-[var(--border)]">
    <div className="flex flex-col gap-1">
      {tier.callout.map((c, i) => (
        <span key={c} className={`font-display tracking-wide ${i === 0 ? "text-xs text-dim" : "text-sm font-600 text-white"}`}>
          {c}
        </span>
      ))}
    </div>
  </div>
);

const ctaLabel = (soldOut, loading, label) => {
  if (soldOut) return "SOLD OUT";
  return loading ? "REDIRECTING…" : label;
};

const CheckoutButton = ({ tier, variant, soldOut }) => {
  const [loading, setLoading] = useState(false);
  return (
    <motion.button
      whileTap={TAP}
      onClick={() => startCheckout(tier.lookupKey, setLoading)}
      disabled={loading || soldOut}
      className={`mt-6 w-full py-4 px-4 text-sm ${ctaClasses[variant]} disabled:opacity-60`}
      data-testid={`checkout-btn-${tier.id}`}
    >
      {ctaLabel(soldOut, loading, tier.cta)}
    </motion.button>
  );
};

export const PricingCard = ({ tier, availability }) => {
  const variant = getAccentVariant(tier.accent);
  const isPro = variant === "pro";
  const isGold = variant === "gold";
  const accent = accentMap[tier.accent] || accentMap.cyan;
  const { ref, rx, ry, onMove, onLeave } = useCardTilt();

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
      className={`relative ${isPro ? "lg:scale-[1.06] lg:-my-2 z-10" : "z-0"}`}
      data-testid={`pricing-card-${tier.id}`}
    >
      {isPro && (
        <div
          className="absolute -inset-3 rounded-none pointer-events-none"
          style={{ background: "radial-gradient(60% 60% at 50% 30%, rgba(0,212,255,0.22), rgba(0,230,118,0.12), transparent 70%)", filter: "blur(24px)" }}
        />
      )}

      <div
        className={`relative h-full flex flex-col glass p-7 sm:p-8 ${isPro ? "glow-cyan" : ""}`}
        style={borderStyles[variant]}
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent.line}, transparent)` }} />

        <Badges tier={tier} />
        <PriceBlock tier={tier} variant={variant} isGold={isGold} />
        <CreditBlock tier={tier} accent={accent} />

        <p className="mt-5 text-sm text-dim leading-relaxed">{tier.support}</p>

        {tier.highlight && (
          <p className="mt-4 font-display text-sm font-600 leading-snug text-emerald" data-testid={`highlight-${tier.id}`}>
            {tier.highlight}
          </p>
        )}

        <Benefits tier={tier} accent={accent} />
        <Callout tier={tier} />

        <CheckoutButton tier={tier} variant={variant} soldOut={availability?.sold_out} />

        {tier.microcopy && (
          <p className="mt-3 text-center text-xs text-dim" data-testid={`microcopy-${tier.id}`}>{tier.microcopy}</p>
        )}
      </div>
    </motion.div>
  );
};
